const VISITOR_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Create an RFC 4122 v4 UUID for the visitor id.
 *
 * Prefers WebCrypto (`crypto.randomUUID` / `getRandomValues`), available in
 * browsers and the Convex runtime. React Native (Hermes) has no global `crypto`
 * unless polyfilled — and referencing it directly throws a ReferenceError — so
 * fall back to a Math.random-based v4. Collision resistance is more than enough
 * for an analytics visitor id.
 */
export function createVisitorId(): string {
  const webCrypto =
    typeof globalThis !== "undefined" ? globalThis.crypto : undefined;

  if (webCrypto?.randomUUID) {
    return webCrypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (webCrypto?.getRandomValues) {
    webCrypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40; // version 4
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // RFC 4122 variant

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return (
    hex.slice(0, 8) +
    "-" +
    hex.slice(8, 12) +
    "-" +
    hex.slice(12, 16) +
    "-" +
    hex.slice(16, 20) +
    "-" +
    hex.slice(20)
  );
}

export function normalizeVisitorId(value: string | null | undefined) {
  const trimmed = value?.trim();
  if (!trimmed || !VISITOR_ID_PATTERN.test(trimmed)) {
    return null;
  }
  return trimmed;
}
